import React, { useState } from 'react';
import { Card } from '../components/ui/Card';
import SafeIcon from '../components/common/SafeIcon';
import * as FiIcons from 'react-icons/fi';
import { clsx } from 'clsx';

export const SystemArchitecture = () => {
  const [activeSection, setActiveSection] = useState('overview');

  const sections = [
    { id: 'overview', label: 'System Overview', icon: FiIcons.FiGrid },
    { id: 'data-flow', label: 'Data Flow', icon: FiIcons.FiGitBranch },
    { id: 'user-roles', label: 'User Roles & Access', icon: FiIcons.FiUsers },
    { id: 'modules', label: 'Module Interactions', icon: FiIcons.FiLayers },
    { id: 'compliance', label: 'Compliance Engine', icon: FiIcons.FiShield },
    { id: 'escalation', label: 'Escalation Logic', icon: FiIcons.FiAlertTriangle },
    { id: 'reporting', label: 'Reporting Pipeline', icon: FiIcons.FiFileText }
  ];

  const dataFlowStages = [
    {
      stage: 1,
      title: 'Data Entry & Capture',
      description: 'Multiple entry points for different data types',
      inputs: [
        { source: 'Ministry Officials', data: 'Project updates, milestone completion, evidence uploads', frequency: 'Daily/Weekly' },
        { source: 'Procurement Officers', data: 'Contract awards, tender publications, funds certifications', frequency: 'Continuous' },
        { source: 'Finance Officers', data: 'Budget execution, payment certificates, virements', frequency: 'Monthly' },
        { source: 'Project Managers', data: 'Progress reports, risk logs, quality assessments', frequency: 'Weekly/Monthly' },
        { source: 'External Systems', data: 'IFMIS integration, PPU portal sync, AG audit findings', frequency: 'Real-time/Batch' }
      ],
      validation: [
        'Mandatory field checks',
        'Data type validation',
        'Legal basis verification',
        'Evidence attachment requirements',
        'Digital signature validation'
      ]
    },
    {
      stage: 2,
      title: 'Data Validation & Processing',
      description: 'Automated validation and enrichment layer',
      processes: [
        {
          name: 'Schema Validation',
          details: 'Ensures all data conforms to predefined schemas (project structure, compliance records, financial transactions)'
        },
        {
          name: 'Legal Compliance Check',
          details: 'Cross-references inputs against legal frameworks (PPA, SFA, NDP6 IMEP) to flag non-compliance'
        },
        {
          name: 'Deadline Calculation',
          details: 'Automatically calculates statutory deadlines based on legal basis (e.g., 7 days for award publication)'
        },
        {
          name: 'NDP6 Tagging',
          details: 'Links projects and activities to NDP6 pillars, thematic areas, and focus areas using taxonomy'
        },
        {
          name: 'Risk Scoring',
          details: 'Applies predictive models to assess project risk (budget overrun, timeline delays, abandonment risk)'
        }
      ],
      outputs: [
        'Validated records stored in central database',
        'Compliance timers activated',
        'Risk flags generated',
        'Alerts queued for escalation engine'
      ]
    },
    {
      stage: 3,
      title: 'Real-Time Monitoring & Alerting',
      description: 'Continuous monitoring engine scanning for compliance breaches',
      monitoring: [
        {
          trigger: 'Deadline Approaching',
          condition: '≤5 days to statutory deadline',
          action: 'Yellow warning alert sent to responsible officer',
          escalation: 'None (informational only)'
        },
        {
          trigger: 'Deadline Critical',
          condition: '≤2 days to statutory deadline',
          action: 'Red critical alert sent to responsible officer + supervisor',
          escalation: 'Level 1 escalation to Directorate'
        },
        {
          trigger: 'Deadline Breach',
          condition: 'Deadline passed without compliance',
          action: 'Breach alert sent to OPM + PPU/AG/relevant authority',
          escalation: 'Level 2 escalation to Permanent Secretary + automatic Cabinet notification'
        },
        {
          trigger: 'Project Inactivity',
          condition: 'No activity logged for 45 days',
          action: 'Abandonment risk alert sent to ministry + OPM',
          escalation: 'Investigation initiated by Prime Minister\'s Office'
        },
        {
          trigger: 'Budget Execution Low',
          condition: 'Execution rate <40% at mid-year',
          action: 'Underspending alert sent to ministry + Ministry of Finance',
          escalation: 'Budget acceleration plan required within 14 days'
        }
      ],
      alertChannels: [
        'In-system dashboard notifications',
        'Email alerts to registered users',
        'SMS alerts for critical breaches (optional)',
        'Automated escalation to oversight bodies (PPU, AG, ACC)'
      ]
    },
    {
      stage: 4,
      title: 'Escalation & Workflow Management',
      description: 'Multi-level escalation hierarchy with accountability tracking',
      levels: [
        {
          level: 0,
          name: 'Informational Alert',
          recipients: 'Responsible officer (e.g., Procurement Officer, Project Manager)',
          timeline: 'N/A',
          action: 'Review and take corrective action',
          consequence: 'None (early warning only)'
        },
        {
          level: 1,
          name: 'Directorate Escalation',
          recipients: 'Director + Responsible Officer',
          timeline: 'Triggered at ≤2 days to deadline or first breach',
          action: 'Formal written response required within 48 hours',
          consequence: 'Non-compliance notice issued if no response'
        },
        {
          level: 2,
          name: 'Permanent Secretary Escalation',
          recipients: 'Permanent Secretary + Director + OPM',
          timeline: 'Triggered at deadline breach or second violation',
          action: 'Emergency intervention plan required within 72 hours',
          consequence: 'Ministerial briefing + potential legal action'
        },
        {
          level: 3,
          name: 'Ministerial/Cabinet Escalation',
          recipients: 'Minister + Permanent Secretary + Prime Minister + Cabinet',
          timeline: 'Triggered at critical breaches (e.g., project abandonment, major audit findings)',
          action: 'Cabinet briefing + formal investigation',
          consequence: 'Disciplinary action, contract termination, legal proceedings'
        }
      ],
      workflowSteps: [
        '1. Alert generated by monitoring engine',
        '2. Escalation level determined by severity + history',
        '3. Notifications sent to all parties in hierarchy',
        '4. Acknowledgment deadline set (24-72 hours)',
        '5. Response logged and tracked',
        '6. Resolution verified or further escalation triggered',
        '7. Permanent audit trail created for all actions'
      ]
    },
    {
      stage: 5,
      title: 'Reporting & Analytics',
      description: 'Automated report generation and performance analytics',
      reportTypes: [
        {
          type: 'Project Audit Pack',
          trigger: 'On-demand or quarterly',
          content: 'Complete audit trail (contracts, payments, milestones, evidence, compliance log)',
          recipients: 'Office of the Auditor General, PPU, Cabinet',
          format: 'PDF with digital signatures + supporting attachments'
        },
        {
          type: 'NDP6 Quarterly Report',
          trigger: 'Quarterly (30 days after period end)',
          content: 'Consolidated progress across 4 pillars, ministry performance, budget execution, gaps',
          recipients: 'Cabinet, Parliament, National Planning Commission',
          format: 'PDF + Excel data annexes'
        },
        {
          type: 'Compliance Report',
          trigger: 'Monthly',
          content: 'Procurement compliance, deadline breaches, non-compliance notices, ministry scorecard',
          recipients: 'Prime Minister\'s Office, Ministry of Finance, PPU',
          format: 'PDF with executive summary dashboard'
        },
        {
          type: 'Ministry Performance Report',
          trigger: 'Quarterly',
          content: 'Ministerial KPI achievement, service delivery standards, performance agreement progress',
          recipients: 'Cabinet, All Permanent Secretaries',
          format: 'PDF with individual ministry scorecards'
        }
      ],
      analytics: [
        'Real-time dashboards (national, ministry, project-level)',
        'Trend analysis (quarterly progress, budget execution, compliance rates)',
        'Predictive risk modeling (project abandonment, deadline breach probability)',
        'Benchmarking (ministry-to-ministry, contractor performance)',
        'NDP6 alignment tracking (pillar progress, thematic area performance)'
      ]
    },
    {
      stage: 6,
      title: 'Audit Trail & Accountability',
      description: 'Immutable record-keeping for transparency and accountability',
      auditLog: [
        { event: 'User Login/Logout', logged: 'Timestamp, user ID, IP address, session duration' },
        { event: 'Data Entry/Update', logged: 'Timestamp, user ID, field changed, old value, new value, justification' },
        { event: 'Evidence Upload', logged: 'Timestamp, user ID, file hash (SHA-256), metadata, GPS location (if available)' },
        { event: 'Alert Generation', logged: 'Timestamp, alert type, severity, recipients, legal basis' },
        { event: 'Escalation Triggered', logged: 'Timestamp, escalation level, recipients, acknowledgment status, response' },
        { event: 'Report Generation', logged: 'Timestamp, report type, generated by, recipients, file hash' },
        { event: 'System Configuration Change', logged: 'Timestamp, user ID, setting changed, old value, new value, approval' }
      ],
      integrity: [
        'All audit logs are write-once (immutable)',
        'Cryptographic hashing of evidence files (SHA-256) prevents tampering',
        'Digital signatures for critical documents (contracts, reports, escalations)',
        'Blockchain-ready architecture for future implementation',
        'Regular automated backups with geographic redundancy'
      ],
      accessibility: [
        'Office of the Auditor General: Full read access to all audit logs',
        'Prime Minister\'s Office: Full access to all system data and logs',
        'Parliament: Access to published reports and escalation summaries',
        'Public: Access to non-confidential reports via public portal (future phase)'
      ]
    }
  ];

  const userRoles = [
    {
      role: 'Prime Minister\'s Office (Super Admin)',
      access: 'Full system access',
      permissions: [
        'View all projects, budgets, compliance records across all ministries',
        'Generate and publish all report types',
        'Initiate escalations and investigations',
        'Configure system settings and user roles',
        'Access full audit trail and logs'
      ],
      responsibilities: [
        'Overall system oversight and governance',
        'Cabinet reporting and briefings',
        'Inter-ministerial coordination',
        'Escalation management'
      ]
    },
    {
      role: 'Ministry Leadership (Minister, Permanent Secretary)',
      access: 'Ministry-level access',
      permissions: [
        'View all projects and data within their ministry',
        'Approve ministry submissions (NDP6 reports, performance updates)',
        'Respond to escalations and compliance alerts',
        'Generate ministry-specific reports',
        'View ministry performance dashboards'
      ],
      responsibilities: [
        'Ensure ministry compliance with all statutory deadlines',
        'Review and approve quarterly NDP6 reports',
        'Address escalated alerts and breaches',
        'Sign off on ministerial performance agreements'
      ]
    },
    {
      role: 'Directorate (Directors, Chief Officers)',
      access: 'Directorate/department-level access',
      permissions: [
        'View and update projects under their directorate',
        'Submit progress reports and evidence',
        'Acknowledge and respond to alerts',
        'Generate directorate-level reports',
        'Manage project teams and assignments'
      ],
      responsibilities: [
        'Day-to-day project monitoring and reporting',
        'Compliance with procurement and financial regulations',
        'Timely submission of required documentation',
        'First-level response to system alerts'
      ]
    },
    {
      role: 'Project Managers & Officers',
      access: 'Project-level access',
      permissions: [
        'Update project progress and milestones',
        'Upload evidence (photos, documents, certificates)',
        'Log risks, issues, and change requests',
        'View project-specific compliance timers',
        'Submit milestone completion reports'
      ],
      responsibilities: [
        'Accurate and timely project data entry',
        'Evidence documentation and validation',
        'Risk identification and mitigation',
        'Contractor performance monitoring'
      ]
    },
    {
      role: 'Procurement Officers',
      access: 'Procurement module access',
      permissions: [
        'Record contract awards and tender publications',
        'Submit funds certification requests',
        'Upload procurement documentation',
        'Track procurement compliance deadlines',
        'Generate procurement reports'
      ],
      responsibilities: [
        'Compliance with Public Procurement Act and Regulations',
        'Timely award publication (7-day rule)',
        'Accurate contract documentation',
        'Liaison with Public Procurement Unit'
      ]
    },
    {
      role: 'Finance Officers',
      access: 'Financial module access',
      permissions: [
        'Record budget allocations and expenditures',
        'Submit payment certificates and invoices',
        'Process virements and reallocations',
        'Track budget execution rates',
        'Generate financial reports'
      ],
      responsibilities: [
        'Accurate financial record-keeping',
        'Budget execution monitoring',
        'Compliance with State Finance Act',
        'Monthly expenditure reporting'
      ]
    },
    {
      role: 'Oversight Bodies (PPU, AG, ACC, Parliament)',
      access: 'Read-only oversight access',
      permissions: [
        'View all compliance records and alerts',
        'Access escalation logs and responses',
        'Download audit packs and reports',
        'View ministry performance scorecards',
        'Access audit trail and system logs'
      ],
      responsibilities: [
        'Independent monitoring and verification',
        'Audit and investigation support',
        'Compliance enforcement',
        'Parliamentary oversight and reporting'
      ]
    },
    {
      role: 'Public (Future Phase)',
      access: 'Public portal (read-only)',
      permissions: [
        'View published NDP6 progress reports',
        'Access non-confidential project information',
        'Download public reports and statistics',
        'View ministry performance rankings',
        'Submit public feedback and petitions'
      ],
      responsibilities: [
        'Civic engagement and accountability',
        'Public oversight and transparency',
        'Feedback on service delivery'
      ]
    }
  ];

  const moduleInteractions = [
    {
      module: 'Project Registry',
      feeds: ['Compliance Deadlines', 'NDP6 Progress', 'Reports & Audit', 'Alerts'],
      receives: ['Procurement (contract data)', 'Budget Execution (financial data)', 'Ministry Compliance (performance data)'],
      keyData: 'Project details, milestones, progress, contractors, budget, NDP6 alignment'
    },
    {
      module: 'Compliance Deadlines',
      feeds: ['Alerts & Escalations', 'Ministry Compliance', 'Reports & Audit'],
      receives: ['Project Registry (project start dates)', 'Procurement (award dates)', 'OMA Mandate (reporting deadlines)'],
      keyData: 'Statutory deadlines, legal basis, countdown timers, breach status'
    },
    {
      module: 'Procurement Oversight',
      feeds: ['Project Registry', 'Compliance Deadlines', 'Alerts', 'Reports'],
      receives: ['External PPU Portal (non-compliance notices)', 'Ministry submissions (procurement plans)'],
      keyData: 'Contract awards, tender publications, procurement plans, contractor performance'
    },
    {
      module: 'NDP6 Progress',
      feeds: ['Dashboard (national overview)', 'Ministry Compliance', 'Reports'],
      receives: ['Project Registry (project progress)', 'Budget Execution (financial data)', 'All ministries (quarterly submissions)'],
      keyData: 'Pillar progress, thematic area performance, KPI achievement, budget execution, gaps'
    },
    {
      module: 'Alerts & Escalations',
      feeds: ['All modules (as notifications)', 'Reports (escalation logs)', 'Audit Trail'],
      receives: ['Compliance Deadlines (breach triggers)', 'Project Registry (risk flags)', 'Budget Execution (underspending alerts)'],
      keyData: 'Alert type, severity, recipients, acknowledgment status, resolution, escalation level'
    },
    {
      module: 'Ministry Compliance',
      feeds: ['Dashboard (ministry rankings)', 'Reports', 'OMA Mandate'],
      receives: ['All modules (aggregated compliance data)', 'Performance Management (KPI data)', 'Audit (AG findings)'],
      keyData: 'Ministry scorecard, compliance areas, KPIs, escalations, recent activity'
    },
    {
      module: 'Reports & Audit',
      feeds: ['External systems (AG, Parliament, Cabinet)', 'Public Portal (future)'],
      receives: ['All modules (data aggregation)', 'Audit Trail (logs)', 'Evidence Vault (attachments)'],
      keyData: 'Audit packs, NDP6 reports, compliance reports, performance reports, investigation reports'
    }
  ];

  const complianceEngine = {
    legalFrameworks: [
      {
        act: 'Public Procurement Act (2015)',
        regulations: 'Public Procurement Regulations (2017)',
        keyDeadlines: [
          { rule: 'Contract award publication', days: 7, basis: 'PP Regs 2017, Reg 42(3)' },
          { rule: 'PPU non-compliance acknowledgment', days: 7, basis: 'PPA S.7(4)' },
          { rule: 'Annual procurement plan submission', days: 60, basis: 'PPA S.22(1)' },
          { rule: 'Funds certification', days: 14, basis: 'SFA S.14' }
        ],
        automation: 'System auto-calculates deadlines based on contract award date or fiscal year start'
      },
      {
        act: 'State Finance Act (1991)',
        regulations: 'Treasury Instructions & Circulars',
        keyDeadlines: [
          { rule: 'Monthly expenditure reports', days: 10, basis: 'Treasury Instructions' },
          { rule: 'Quarterly budget execution reports', days: 30, basis: 'Treasury Circular 2024/05' },
          { rule: 'Virement processing', days: 30, basis: 'SFA S.14' },
          { rule: 'Annual financial statements', days: 90, basis: 'SFA S.27' }
        ],
        automation: 'System triggers alerts based on fiscal calendar (month-end, quarter-end, year-end)'
      },
      {
        act: 'NDP6 IMEP Framework (2024)',
        regulations: 'National Planning Commission Guidelines',
        keyDeadlines: [
          { rule: 'Quarterly NDP6 progress reports', days: 30, basis: 'NDP6 IMEP Framework' },
          { rule: 'Annual NDP6 review', days: 90, basis: 'NDP6 IMEP Framework' },
          { rule: 'Mid-term NDP6 evaluation', days: 180, basis: 'NDP6 IMEP Framework' }
        ],
        automation: 'System tracks reporting periods and auto-generates submission deadlines'
      },
      {
        act: 'Audit Act (2005)',
        regulations: 'Office of the Auditor General Guidelines',
        keyDeadlines: [
          { rule: 'AG recommendation implementation', days: 180, basis: 'Audit Act S.27' },
          { rule: 'Audit query responses', days: 30, basis: 'AG Guidelines' },
          { rule: 'Audit committee meetings', days: 90, basis: 'Internal Audit Guidelines' }
        ],
        automation: 'System tracks AG findings and monitors implementation progress'
      }
    ],
    calculationLogic: [
      '1. Identify triggering event (e.g., contract award date, fiscal period start)',
      '2. Lookup applicable legal basis from compliance matrix',
      '3. Calculate deadline date (trigger date + allowed days)',
      '4. Set alert thresholds (5 days warning, 2 days critical)',
      '5. Monitor daily for deadline approach/breach',
      '6. Generate alerts and escalations based on status',
      '7. Log all compliance events in audit trail'
    ]
  };

  return (
    <div className="space-y-6 max-w-7xl mx-auto">
      {/* Header */}
      <div className="flex flex-col md:flex-row justify-between items-start md:items-center gap-4">
        <div>
          <h1 className="text-2xl font-bold text-slate-900">System Architecture & Data Flow</h1>
          <p className="text-slate-500">
            Comprehensive technical documentation of how GovTrack works
          </p>
        </div>
        <button className="px-4 py-2 border border-slate-300 rounded-lg text-sm font-medium hover:bg-slate-50 flex items-center gap-2">
          <SafeIcon icon={FiIcons.FiDownload} />
          Download Technical Spec
        </button>
      </div>

      {/* Navigation Tabs */}
      <Card>
        <div className="flex gap-2 overflow-x-auto pb-2">
          {sections.map(section => (
            <button
              key={section.id}
              onClick={() => setActiveSection(section.id)}
              className={clsx(
                'px-4 py-2 rounded-lg text-sm font-medium transition-all flex items-center gap-2 whitespace-nowrap',
                activeSection === section.id
                  ? 'bg-grn-navy text-white'
                  : 'bg-white border border-slate-200 text-slate-600 hover:border-grn-navy/30'
              )}
            >
              <SafeIcon icon={section.icon} />
              {section.label}
            </button>
          ))}
        </div>
      </Card>

      {/* System Overview */}
      {activeSection === 'overview' && (
        <>
          <Card className="bg-gradient-to-r from-grn-navy to-blue-900 text-white">
            <div className="flex items-start gap-4">
              <SafeIcon icon={FiIcons.FiInfo} className="text-3xl shrink-0 mt-1" />
              <div>
                <h2 className="text-2xl font-bold mb-4">System Architecture Overview</h2>
                <p className="text-blue-100 mb-4">
                  GovTrack is a <strong>centralized, real-time monitoring and accountability platform</strong> designed to enforce compliance,
                  accelerate project delivery, and ensure transparent governance across all Namibian government ministries.
                </p>
                <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mt-6">
                  <div className="p-4 bg-white/10 rounded-lg backdrop-blur-sm border border-white/20">
                    <h3 className="font-bold mb-2 flex items-center gap-2">
                      <SafeIcon icon={FiIcons.FiDatabase} />
                      Data Architecture
                    </h3>
                    <p className="text-sm text-blue-100">
                      Centralized PostgreSQL database with real-time sync, event-driven architecture, and immutable audit logs
                    </p>
                  </div>
                  <div className="p-4 bg-white/10 rounded-lg backdrop-blur-sm border border-white/20">
                    <h3 className="font-bold mb-2 flex items-center gap-2">
                      <SafeIcon icon={FiIcons.FiCpu} />
                      Processing Engine
                    </h3>
                    <p className="text-sm text-blue-100">
                      Automated compliance monitoring, predictive risk modeling, and multi-level escalation workflows
                    </p>
                  </div>
                  <div className="p-4 bg-white/10 rounded-lg backdrop-blur-sm border border-white/20">
                    <h3 className="font-bold mb-2 flex items-center gap-2">
                      <SafeIcon icon={FiIcons.FiShield} />
                      Security & Access
                    </h3>
                    <p className="text-sm text-blue-100">
                      Role-based access control (RBAC), digital signatures, cryptographic hashing, and comprehensive audit trails
                    </p>
                  </div>
                </div>
              </div>
            </div>
          </Card>

          {/* High-Level System Diagram */}
          <Card title="High-Level System Diagram">
            <div className="space-y-6">
              {/* Input Layer */}
              <div className="border-l-4 border-l-blue-500 pl-4">
                <h3 className="text-lg font-bold text-slate-900 mb-3 flex items-center gap-2">
                  <SafeIcon icon={FiIcons.FiDownload} className="text-blue-600" />
                  Input Layer (Data Entry Points)
                </h3>
                <div className="grid grid-cols-1 md:grid-cols-4 gap-3">
                  {['Ministry Web Portals', 'Mobile Apps (Field)', 'External System APIs', 'Bulk Upload (Excel/CSV)'].map((input, idx) => (
                    <div key={idx} className="p-3 bg-blue-50 border border-blue-200 rounded-lg text-sm text-center">
                      {input}
                    </div>
                  ))}
                </div>
              </div>

              {/* Processing Layer */}
              <div className="border-l-4 border-l-green-500 pl-4">
                <h3 className="text-lg font-bold text-slate-900 mb-3 flex items-center gap-2">
                  <SafeIcon icon={FiIcons.FiCpu} className="text-green-600" />
                  Processing Layer (Business Logic)
                </h3>
                <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
                  {[
                    'Validation Engine',
                    'Compliance Calculator',
                    'Risk Scoring Model',
                    'NDP6 Taxonomy Mapper',
                    'Escalation Workflow',
                    'Report Generator'
                  ].map((process, idx) => (
                    <div key={idx} className="p-3 bg-green-50 border border-green-200 rounded-lg text-sm text-center">
                      {process}
                    </div>
                  ))}
                </div>
              </div>

              {/* Storage Layer */}
              <div className="border-l-4 border-l-amber-500 pl-4">
                <h3 className="text-lg font-bold text-slate-900 mb-3 flex items-center gap-2">
                  <SafeIcon icon={FiIcons.FiDatabase} className="text-amber-600" />
                  Storage Layer (Data Persistence)
                </h3>
                <div className="grid grid-cols-1 md:grid-cols-4 gap-3">
                  {['Project Database', 'Compliance Logs', 'Evidence Vault', 'Audit Trail'].map((storage, idx) => (
                    <div key={idx} className="p-3 bg-amber-50 border border-amber-200 rounded-lg text-sm text-center">
                      {storage}
                    </div>
                  ))}
                </div>
              </div>

              {/* Output Layer */}
              <div className="border-l-4 border-l-red-500 pl-4">
                <h3 className="text-lg font-bold text-slate-900 mb-3 flex items-center gap-2">
                  <SafeIcon icon={FiIcons.FiUpload} className="text-red-600" />
                  Output Layer (Interfaces)
                </h3>
                <div className="grid grid-cols-1 md:grid-cols-4 gap-3">
                  {['Dashboards', 'Alerts & Notifications', 'Reports (PDF/Excel)', 'API Endpoints'].map((output, idx) => (
                    <div key={idx} className="p-3 bg-red-50 border border-red-200 rounded-lg text-sm text-center">
                      {output}
                    </div>
                  ))}
                </div>
              </div>
            </div>
          </Card>
        </>
      )}

      {/* Data Flow */}
      {activeSection === 'data-flow' && (
        <div className="space-y-6">
          <Card className="bg-blue-50 border-blue-200">
            <div className="flex gap-3">
              <SafeIcon icon={FiIcons.FiGitBranch} className="text-grn-navy text-2xl shrink-0 mt-1" />
              <div>
                <h2 className="text-xl font-bold text-slate-900 mb-2">Data Flow Pipeline</h2>
                <p className="text-slate-700">
                  The following stages illustrate how data moves through the GovTrack system, from initial entry to final reporting and audit.
                </p>
              </div>
            </div>
          </Card>

          {dataFlowStages.map((stage, idx) => (
            <Card key={idx} className="border-l-4 border-l-grn-navy">
              <div className="flex items-start gap-4 mb-4">
                <div className="flex items-center justify-center w-12 h-12 bg-grn-navy text-white rounded-full text-xl font-black shrink-0">
                  {stage.stage}
                </div>
                <div className="flex-1">
                  <h3 className="text-xl font-bold text-slate-900 mb-1">{stage.title}</h3>
                  <p className="text-slate-600">{stage.description}</p>
                </div>
              </div>

              {stage.inputs && (
                <div className="mb-4">
                  <h4 className="text-sm font-bold text-slate-700 uppercase tracking-wider mb-3">Data Inputs</h4>
                  <div className="space-y-2">
                    {stage.inputs.map((input, iIdx) => (
                      <div key={iIdx} className="flex items-start gap-3 p-3 bg-slate-50 border border-slate-200 rounded-lg">
                        <SafeIcon icon={FiIcons.FiUser} className="text-grn-navy mt-1 shrink-0" />
                        <div className="flex-1">
                          <div className="flex items-center justify-between mb-1">
                            <span className="font-bold text-slate-800 text-sm">{input.source}</span>
                            <span className="text-xs px-2 py-1 bg-blue-100 text-blue-700 rounded">{input.frequency}</span>
                          </div>
                          <p className="text-sm text-slate-600">{input.data}</p>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {stage.validation && (
                <div className="mb-4">
                  <h4 className="text-sm font-bold text-slate-700 uppercase tracking-wider mb-3">Validation Checks</h4>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-2">
                    {stage.validation.map((check, vIdx) => (
                      <div key={vIdx} className="flex items-center gap-2 p-2 bg-green-50 border border-green-200 rounded text-sm">
                        <SafeIcon icon={FiIcons.FiCheckCircle} className="text-green-600 shrink-0" />
                        <span className="text-slate-700">{check}</span>
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {stage.processes && (
                <div className="mb-4">
                  <h4 className="text-sm font-bold text-slate-700 uppercase tracking-wider mb-3">Processing Steps</h4>
                  <div className="space-y-3">
                    {stage.processes.map((process, pIdx) => (
                      <div key={pIdx} className="p-3 bg-amber-50 border border-amber-200 rounded-lg">
                        <h5 className="font-bold text-slate-800 mb-1 flex items-center gap-2">
                          <SafeIcon icon={FiIcons.FiZap} className="text-amber-600" />
                          {process.name}
                        </h5>
                        <p className="text-sm text-slate-600">{process.details}</p>
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {stage.monitoring && (
                <div className="mb-4">
                  <h4 className="text-sm font-bold text-slate-700 uppercase tracking-wider mb-3">Monitoring Triggers</h4>
                  <div className="space-y-3">
                    {stage.monitoring.map((monitor, mIdx) => (
                      <div key={mIdx} className="p-3 bg-red-50 border border-red-200 rounded-lg">
                        <div className="flex items-start justify-between mb-2">
                          <h5 className="font-bold text-red-900 flex items-center gap-2">
                            <SafeIcon icon={FiIcons.FiAlertCircle} className="text-red-600" />
                            {monitor.trigger}
                          </h5>
                          <span className="text-xs px-2 py-1 bg-red-600 text-white rounded font-bold">
                            {monitor.condition}
                          </span>
                        </div>
                        <div className="grid grid-cols-1 md:grid-cols-2 gap-2 text-xs">
                          <div>
                            <span className="text-slate-500 font-medium">Action:</span>
                            <p className="text-slate-700 mt-1">{monitor.action}</p>
                          </div>
                          <div>
                            <span className="text-slate-500 font-medium">Escalation:</span>
                            <p className="text-slate-700 mt-1">{monitor.escalation}</p>
                          </div>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {stage.alertChannels && (
                <div className="mb-4">
                  <h4 className="text-sm font-bold text-slate-700 uppercase tracking-wider mb-3">Alert Channels</h4>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-2">
                    {stage.alertChannels.map((channel, cIdx) => (
                      <div key={cIdx} className="flex items-center gap-2 p-2 bg-blue-50 border border-blue-200 rounded text-sm">
                        <SafeIcon icon={FiIcons.FiBell} className="text-blue-600 shrink-0" />
                        <span className="text-slate-700">{channel}</span>
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {stage.levels && (
                <div className="mb-4">
                  <h4 className="text-sm font-bold text-slate-700 uppercase tracking-wider mb-3">Escalation Levels</h4>
                  <div className="space-y-3">
                    {stage.levels.map((level, lIdx) => (
                      <div key={lIdx} className="p-4 bg-slate-50 border border-slate-200 rounded-lg">
                        <div className="flex items-center gap-3 mb-3">
                          <span className={clsx(
                            'px-3 py-1 rounded font-bold text-sm',
                            level.level === 0 ? 'bg-blue-100 text-blue-700' :
                            level.level === 1 ? 'bg-amber-100 text-amber-700' :
                            level.level === 2 ? 'bg-orange-100 text-orange-700' :
                            'bg-red-100 text-red-700'
                          )}>
                            LEVEL {level.level}
                          </span>
                          <h5 className="font-bold text-slate-900">{level.name}</h5>
                        </div>
                        <div className="grid grid-cols-1 md:grid-cols-2 gap-3 text-sm">
                          <div>
                            <span className="text-slate-500 font-medium">Recipients:</span>
                            <p className="text-slate-800 mt-1">{level.recipients}</p>
                          </div>
                          <div>
                            <span className="text-slate-500 font-medium">Timeline:</span>
                            <p className="text-slate-800 mt-1">{level.timeline}</p>
                          </div>
                          <div>
                            <span className="text-slate-500 font-medium">Required Action:</span>
                            <p className="text-slate-800 mt-1">{level.action}</p>
                          </div>
                          <div>
                            <span className="text-slate-500 font-medium">Consequence:</span>
                            <p className="text-slate-800 mt-1">{level.consequence}</p>
                          </div>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {stage.workflowSteps && (
                <div className="mb-4">
                  <h4 className="text-sm font-bold text-slate-700 uppercase tracking-wider mb-3">Workflow Steps</h4>
                  <div className="space-y-2">
                    {stage.workflowSteps.map((step, sIdx) => (
                      <div key={sIdx} className="flex items-center gap-3 p-2 bg-slate-50 border border-slate-200 rounded text-sm">
                        <span className="flex items-center justify-center w-6 h-6 bg-grn-navy text-white rounded-full text-xs font-bold shrink-0">
                          {sIdx + 1}
                        </span>
                        <span className="text-slate-700">{step.replace(/^\d+\.\s*/, '')}</span>
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {stage.reportTypes && (
                <div className="mb-4">
                  <h4 className="text-sm font-bold text-slate-700 uppercase tracking-wider mb-3">Report Types</h4>
                  <div className="space-y-3">
                    {stage.reportTypes.map((report, rIdx) => (
                      <div key={rIdx} className="p-3 bg-blue-50 border border-blue-200 rounded-lg">
                        <div className="flex items-start justify-between mb-2">
                          <h5 className="font-bold text-slate-900">{report.type}</h5>
                          <span className="text-xs px-2 py-1 bg-blue-600 text-white rounded">{report.trigger}</span>
                        </div>
                        <p className="text-sm text-slate-600 mb-2">{report.content}</p>
                        <div className="grid grid-cols-1 md:grid-cols-2 gap-2 text-xs">
                          <div>
                            <span className="text-slate-500 font-medium">Recipients:</span>
                            <p className="text-slate-700 mt-1">{report.recipients}</p>
                          </div>
                          <div>
                            <span className="text-slate-500 font-medium">Format:</span>
                            <p className="text-slate-700 mt-1">{report.format}</p>
                          </div>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {stage.analytics && (
                <div className="mb-4">
                  <h4 className="text-sm font-bold text-slate-700 uppercase tracking-wider mb-3">Analytics & Insights</h4>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-2">
                    {stage.analytics.map((analytic, aIdx) => (
                      <div key={aIdx} className="flex items-center gap-2 p-2 bg-purple-50 border border-purple-200 rounded text-sm">
                        <SafeIcon icon={FiIcons.FiTrendingUp} className="text-purple-600 shrink-0" />
                        <span className="text-slate-700">{analytic}</span>
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {stage.auditLog && (
                <div className="mb-4">
                  <h4 className="text-sm font-bold text-slate-700 uppercase tracking-wider mb-3">Audit Log Events</h4>
                  <div className="space-y-2">
                    {stage.auditLog.map((log, lIdx) => (
                      <div key={lIdx} className="flex items-start gap-3 p-3 bg-slate-50 border border-slate-200 rounded-lg">
                        <SafeIcon icon={FiIcons.FiFileText} className="text-slate-400 mt-1 shrink-0" />
                        <div className="flex-1">
                          <h5 className="font-bold text-slate-800 text-sm mb-1">{log.event}</h5>
                          <p className="text-xs text-slate-600">{log.logged}</p>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {stage.integrity && (
                <div className="mb-4">
                  <h4 className="text-sm font-bold text-slate-700 uppercase tracking-wider mb-3">Data Integrity Measures</h4>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-2">
                    {stage.integrity.map((measure, mIdx) => (
                      <div key={mIdx} className="flex items-center gap-2 p-2 bg-green-50 border border-green-200 rounded text-sm">
                        <SafeIcon icon={FiIcons.FiLock} className="text-green-600 shrink-0" />
                        <span className="text-slate-700">{measure}</span>
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {stage.accessibility && (
                <div>
                  <h4 className="text-sm font-bold text-slate-700 uppercase tracking-wider mb-3">Access Rights</h4>
                  <div className="space-y-2">
                    {stage.accessibility.map((access, aIdx) => (
                      <div key={aIdx} className="flex items-start gap-2 p-2 bg-amber-50 border border-amber-200 rounded text-sm">
                        <SafeIcon icon={FiIcons.FiEye} className="text-amber-600 mt-0.5 shrink-0" />
                        <span className="text-slate-700">{access}</span>
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {stage.outputs && (
                <div>
                  <h4 className="text-sm font-bold text-slate-700 uppercase tracking-wider mb-3">Outputs</h4>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-2">
                    {stage.outputs.map((output, oIdx) => (
                      <div key={oIdx} className="flex items-center gap-2 p-2 bg-blue-50 border border-blue-200 rounded text-sm">
                        <SafeIcon icon={FiIcons.FiCheckCircle} className="text-blue-600 shrink-0" />
                        <span className="text-slate-700">{output}</span>
                      </div>
                    ))}
                  </div>
                </div>
              )}
            </Card>
          ))}
        </div>
      )}

      {/* User Roles & Access */}
      {activeSection === 'user-roles' && (
        <div className="space-y-6">
          <Card className="bg-amber-50 border-amber-200">
            <div className="flex gap-3">
              <SafeIcon icon={FiIcons.FiUsers} className="text-grn-navy text-2xl shrink-0 mt-1" />
              <div>
                <h2 className="text-xl font-bold text-slate-900 mb-2">Role-Based Access Control (RBAC)</h2>
                <p className="text-slate-700">
                  GovTrack implements a hierarchical access control system where permissions are granted based on user roles and responsibilities.
                  This ensures data security, accountability, and appropriate access to sensitive information.
                </p>
              </div>
            </div>
          </Card>

          {userRoles.map((role, idx) => (
            <Card key={idx} className="border-l-4 border-l-grn-gold">
              <div className="mb-4">
                <div className="flex items-center justify-between mb-2">
                  <h3 className="text-xl font-bold text-slate-900">{role.role}</h3>
                  <span className="px-3 py-1 bg-grn-navy text-white text-xs font-bold rounded uppercase">
                    {role.access}
                  </span>
                </div>
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <div>
                  <h4 className="text-sm font-bold text-slate-700 uppercase tracking-wider mb-3 flex items-center gap-2">
                    <SafeIcon icon={FiIcons.FiKey} className="text-green-600" />
                    Permissions
                  </h4>
                  <div className="space-y-2">
                    {role.permissions.map((permission, pIdx) => (
                      <div key={pIdx} className="flex items-start gap-2 p-2 bg-green-50 border border-green-200 rounded text-sm">
                        <SafeIcon icon={FiIcons.FiCheckCircle} className="text-green-600 mt-0.5 shrink-0" />
                        <span className="text-slate-700">{permission}</span>
                      </div>
                    ))}
                  </div>
                </div>

                <div>
                  <h4 className="text-sm font-bold text-slate-700 uppercase tracking-wider mb-3 flex items-center gap-2">
                    <SafeIcon icon={FiIcons.FiBriefcase} className="text-blue-600" />
                    Responsibilities
                  </h4>
                  <div className="space-y-2">
                    {role.responsibilities.map((responsibility, rIdx) => (
                      <div key={rIdx} className="flex items-start gap-2 p-2 bg-blue-50 border border-blue-200 rounded text-sm">
                        <SafeIcon icon={FiIcons.FiTarget} className="text-blue-600 mt-0.5 shrink-0" />
                        <span className="text-slate-700">{responsibility}</span>
                      </div>
                    ))}
                  </div>
                </div>
              </div>
            </Card>
          ))}
        </div>
      )}

      {/* Module Interactions */}
      {activeSection === 'modules' && (
        <div className="space-y-6">
          <Card className="bg-purple-50 border-purple-200">
            <div className="flex gap-3">
              <SafeIcon icon={FiIcons.FiLayers} className="text-grn-navy text-2xl shrink-0 mt-1" />
              <div>
                <h2 className="text-xl font-bold text-slate-900 mb-2">Module Interaction Map</h2>
                <p className="text-slate-700">
                  GovTrack modules are interconnected, sharing data and triggering workflows across the system. Understanding these interactions
                  is critical for effective system use and troubleshooting.
                </p>
              </div>
            </div>
          </Card>

          {moduleInteractions.map((module, idx) => (
            <Card key={idx} className="border-l-4 border-l-purple-500">
              <h3 className="text-xl font-bold text-slate-900 mb-4">{module.module}</h3>
              
              <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-4">
                <div className="p-4 bg-blue-50 border border-blue-200 rounded-lg">
                  <h4 className="text-sm font-bold text-blue-900 uppercase tracking-wider mb-3 flex items-center gap-2">
                    <SafeIcon icon={FiIcons.FiArrowRight} className="text-blue-600" />
                    Feeds Data To
                  </h4>
                  <div className="space-y-1">
                    {module.feeds.map((feed, fIdx) => (
                      <div key={fIdx} className="text-sm text-slate-700 flex items-center gap-2">
                        <SafeIcon icon={FiIcons.FiChevronRight} className="text-blue-500 text-xs" />
                        {feed}
                      </div>
                    ))}
                  </div>
                </div>

                <div className="p-4 bg-green-50 border border-green-200 rounded-lg">
                  <h4 className="text-sm font-bold text-green-900 uppercase tracking-wider mb-3 flex items-center gap-2">
                    <SafeIcon icon={FiIcons.FiArrowLeft} className="text-green-600" />
                    Receives Data From
                  </h4>
                  <div className="space-y-1">
                    {module.receives.map((receive, rIdx) => (
                      <div key={rIdx} className="text-sm text-slate-700 flex items-center gap-2">
                        <SafeIcon icon={FiIcons.FiChevronRight} className="text-green-500 text-xs" />
                        {receive}
                      </div>
                    ))}
                  </div>
                </div>

                <div className="p-4 bg-amber-50 border border-amber-200 rounded-lg">
                  <h4 className="text-sm font-bold text-amber-900 uppercase tracking-wider mb-3 flex items-center gap-2">
                    <SafeIcon icon={FiIcons.FiDatabase} className="text-amber-600" />
                    Key Data Managed
                  </h4>
                  <p className="text-sm text-slate-700">{module.keyData}</p>
                </div>
              </div>
            </Card>
          ))}
        </div>
      )}

      {/* Compliance Engine */}
      {activeSection === 'compliance' && (
        <div className="space-y-6">
          <Card className="bg-red-50 border-red-200">
            <div className="flex gap-3">
              <SafeIcon icon={FiIcons.FiShield} className="text-grn-navy text-2xl shrink-0 mt-1" />
              <div>
                <h2 className="text-xl font-bold text-slate-900 mb-2">Compliance Engine Architecture</h2>
                <p className="text-slate-700">
                  The compliance engine is the heart of GovTrack's enforcement mechanism. It continuously monitors all activities against
                  statutory deadlines defined in Namibian law and automatically triggers alerts and escalations.
                </p>
              </div>
            </div>
          </Card>

          {complianceEngine.legalFrameworks.map((framework, idx) => (
            <Card key={idx} className="border-l-4 border-l-red-500">
              <div className="mb-4">
                <h3 className="text-xl font-bold text-slate-900 mb-1">{framework.act}</h3>
                <p className="text-sm text-slate-600 italic">{framework.regulations}</p>
              </div>

              <div className="mb-4">
                <h4 className="text-sm font-bold text-slate-700 uppercase tracking-wider mb-3">Key Statutory Deadlines</h4>
                <div className="space-y-2">
                  {framework.keyDeadlines.map((deadline, dIdx) => (
                    <div key={dIdx} className="flex items-center justify-between p-3 bg-slate-50 border border-slate-200 rounded-lg">
                      <div className="flex-1">
                        <h5 className="font-bold text-slate-800 text-sm mb-1">{deadline.rule}</h5>
                        <p className="text-xs text-slate-500 font-mono">{deadline.basis}</p>
                      </div>
                      <div className="text-right ml-4">
                        <div className="text-2xl font-black text-grn-navy">{deadline.days}</div>
                        <div className="text-xs text-slate-500 uppercase">Days</div>
                      </div>
                    </div>
                  ))}
                </div>
              </div>

              <div className="p-4 bg-blue-50 border border-blue-200 rounded-lg">
                <h4 className="text-sm font-bold text-blue-900 uppercase tracking-wider mb-2 flex items-center gap-2">
                  <SafeIcon icon={FiIcons.FiZap} className="text-blue-600" />
                  Automation Logic
                </h4>
                <p className="text-sm text-slate-700">{framework.automation}</p>
              </div>
            </Card>
          ))}

          <Card className="bg-grn-navy text-white">
            <h3 className="text-xl font-bold mb-4">Deadline Calculation Algorithm</h3>
            <div className="space-y-2">
              {complianceEngine.calculationLogic.map((step, idx) => (
                <div key={idx} className="flex items-center gap-3 p-2 bg-white/10 rounded">
                  <span className="flex items-center justify-center w-6 h-6 bg-grn-gold text-grn-navy rounded-full text-sm font-bold shrink-0">
                    {idx + 1}
                  </span>
                  <span className="text-sm">{step.replace(/^\d+\.\s*/, '')}</span>
                </div>
              ))}
            </div>
          </Card>
        </div>
      )}

      {/* Escalation Logic */}
      {activeSection === 'escalation' && (
        <div className="space-y-6">
          <Card className="bg-orange-50 border-orange-200">
            <div className="flex gap-3">
              <SafeIcon icon={FiIcons.FiAlertTriangle} className="text-grn-navy text-2xl shrink-0 mt-1" />
              <div>
                <h2 className="text-xl font-bold text-slate-900 mb-2">Escalation Workflow Logic</h2>
                <p className="text-slate-700">
                  Escalations are triggered automatically based on severity, response time, and historical compliance. The system ensures
                  accountability by routing alerts to progressively higher levels of authority until resolution.
                </p>
              </div>
            </div>
          </Card>

          <Card title="Escalation Decision Tree">
            <div className="space-y-4">
              <div className="p-4 bg-blue-50 border-l-4 border-l-blue-500 rounded">
                <h4 className="font-bold text-blue-900 mb-2">Trigger Event Detected</h4>
                <p className="text-sm text-slate-700">System identifies a compliance breach, deadline approach, or risk flag</p>
              </div>

              <div className="flex items-center justify-center">
                <SafeIcon icon={FiIcons.FiArrowDown} className="text-2xl text-slate-400" />
              </div>

              <div className="p-4 bg-amber-50 border-l-4 border-l-amber-500 rounded">
                <h4 className="font-bold text-amber-900 mb-2">Severity Assessment</h4>
                <div className="space-y-2 text-sm">
                  <div className="flex items-center gap-2">
                    <SafeIcon icon={FiIcons.FiCircle} className="text-amber-600" />
                    <span className="text-slate-700">Days to deadline: ≤2 days = Critical, 3-5 days = Warning, >5 days = Informational</span>
                  </div>
                  <div className="flex items-center gap-2">
                    <SafeIcon icon={FiIcons.FiCircle} className="text-amber-600" />
                    <span className="text-slate-700">Historical compliance: Prior breaches increase severity</span>
                  </div>
                  <div className="flex items-center gap-2">
                    <SafeIcon icon={FiIcons.FiCircle} className="text-amber-600" />
                    <span className="text-slate-700">Impact assessment: High-value projects or critical NDP6 pillars escalate faster</span>
                  </div>
                </div>
              </div>

              <div className="flex items-center justify-center">
                <SafeIcon icon={FiIcons.FiArrowDown} className="text-2xl text-slate-400" />
              </div>

              <div className="p-4 bg-orange-50 border-l-4 border-l-orange-500 rounded">
                <h4 className="font-bold text-orange-900 mb-2">Escalation Level Determination</h4>
                <div className="space-y-2 text-sm">
                  <div className="flex items-center gap-2">
                    <SafeIcon icon={FiIcons.FiCircle} className="text-orange-600" />
                    <span className="text-slate-700">Level 0: Informational alert to responsible officer</span>
                  </div>
                  <div className="flex items-center gap-2">
                    <SafeIcon icon={FiIcons.FiCircle} className="text-orange-600" />
                    <span className="text-slate-700">Level 1: Escalation to Directorate if no response within 48 hours</span>
                  </div>
                  <div className="flex items-center gap-2">
                    <SafeIcon icon={FiIcons.FiCircle} className="text-orange-600" />
                    <span className="text-slate-700">Level 2: Escalation to Permanent Secretary if breach occurs</span>
                  </div>
                  <div className="flex items-center gap-2">
                    <SafeIcon icon={FiIcons.FiCircle} className="text-orange-600" />
                    <span className="text-slate-700">Level 3: Escalation to Minister/Cabinet for critical breaches or repeated violations</span>
                  </div>
                </div>
              </div>

              <div className="flex items-center justify-center">
                <SafeIcon icon={FiIcons.FiArrowDown} className="text-2xl text-slate-400" />
              </div>

              <div className="p-4 bg-red-50 border-l-4 border-l-red-500 rounded">
                <h4 className="font-bold text-red-900 mb-2">Notification & Tracking</h4>
                <div className="space-y-2 text-sm">
                  <div className="flex items-center gap-2">
                    <SafeIcon icon={FiIcons.FiCircle} className="text-red-600" />
                    <span className="text-slate-700">Alerts sent via email, SMS (optional), and in-system dashboard</span>
                  </div>
                  <div className="flex items-center gap-2">
                    <SafeIcon icon={FiIcons.FiCircle} className="text-red-600" />
                    <span className="text-slate-700">Acknowledgment required within specified timeframe</span>
                  </div>
                  <div className="flex items-center gap-2">
                    <SafeIcon icon={FiIcons.FiCircle} className="text-red-600" />
                    <span className="text-slate-700">All actions logged in immutable audit trail</span>
                  </div>
                  <div className="flex items-center gap-2">
                    <SafeIcon icon={FiIcons.FiCircle} className="text-red-600" />
                    <span className="text-slate-700">Escalation continues until resolution or maximum level reached</span>
                  </div>
                </div>
              </div>

              <div className="flex items-center justify-center">
                <SafeIcon icon={FiIcons.FiArrowDown} className="text-2xl text-slate-400" />
              </div>

              <div className="p-4 bg-green-50 border-l-4 border-l-green-500 rounded">
                <h4 className="font-bold text-green-900 mb-2">Resolution & Closure</h4>
                <p className="text-sm text-slate-700">
                  Once the issue is resolved (e.g., compliance achieved, deadline met, corrective action taken), the escalation is closed
                  and logged. A resolution report is generated and sent to all parties involved.
                </p>
              </div>
            </div>
          </Card>
        </div>
      )}

      {/* Reporting Pipeline */}
      {activeSection === 'reporting' && (
        <div className="space-y-6">
          <Card className="bg-slate-50 border-slate-200">
            <div className="flex gap-3">
              <SafeIcon icon={FiIcons.FiFileText} className="text-grn-navy text-2xl shrink-0 mt-1" />
              <div>
                <h2 className="text-xl font-bold text-slate-900 mb-2">Automated Reporting Pipeline</h2>
                <p className="text-slate-700">
                  GovTrack generates comprehensive reports automatically based on predefined templates, data aggregation rules, and
                  reporting schedules. All reports are digitally signed and include full audit trails.
                </p>
              </div>
            </div>
          </Card>

          <Card title="Report Generation Process">
            <div className="space-y-4">
              {[
                {
                  step: 'Data Aggregation',
                  description: 'System collects data from all relevant modules based on report type (e.g., all NDP6 data for quarterly report)',
                  icon: FiIcons.FiDatabase,
                  color: 'blue'
                },
                {
                  step: 'Validation & Quality Check',
                  description: 'Data completeness verified, outliers flagged, and missing data highlighted for manual review',
                  icon: FiIcons.FiCheckCircle,
                  color: 'green'
                },
                {
                  step: 'Template Application',
                  description: 'Data mapped to predefined report template (PDF/Excel) with charts, tables, and narrative sections',
                  icon: FiIcons.FiLayout,
                  color: 'amber'
                },
                {
                  step: 'Executive Summary Generation',
                  description: 'AI-assisted summary highlighting key findings, trends, and recommendations',
                  icon: FiIcons.FiZap,
                  color: 'purple'
                },
                {
                  step: 'Attachments & Evidence',
                  description: 'Supporting documents, evidence files, and annexes automatically compiled and attached',
                  icon: FiIcons.FiPaperclip,
                  color: 'slate'
                },
                {
                  step: 'Digital Signature & Hashing',
                  description: 'Report cryptographically signed and hashed (SHA-256) to prevent tampering',
                  icon: FiIcons.FiLock,
                  color: 'red'
                },
                {
                  step: 'Distribution & Archiving',
                  description: 'Report sent to designated recipients via email and stored in permanent archive with version control',
                  icon: FiIcons.FiSend,
                  color: 'green'
                }
              ].map((stage, idx) => (
                <div key={idx} className="flex items-start gap-4 p-4 bg-slate-50 border border-slate-200 rounded-lg">
                  <div className={clsx(
                    'flex items-center justify-center w-12 h-12 rounded-full text-white shrink-0',
                    stage.color === 'blue' ? 'bg-blue-500' :
                    stage.color === 'green' ? 'bg-green-500' :
                    stage.color === 'amber' ? 'bg-amber-500' :
                    stage.color === 'purple' ? 'bg-purple-500' :
                    stage.color === 'red' ? 'bg-red-500' :
                    'bg-slate-500'
                  )}>
                    <SafeIcon icon={stage.icon} className="text-xl" />
                  </div>
                  <div className="flex-1">
                    <h4 className="font-bold text-slate-900 mb-1">{stage.step}</h4>
                    <p className="text-sm text-slate-600">{stage.description}</p>
                  </div>
                  <div className="text-2xl font-black text-slate-300">{idx + 1}</div>
                </div>
              ))}
            </div>
          </Card>
        </div>
      )}

      {/* Footer */}
      <Card className="bg-grn-navy text-white">
        <div className="flex gap-3">
          <SafeIcon icon={FiIcons.FiInfo} className="text-xl shrink-0 mt-0.5" />
          <div className="text-xs">
            <p className="font-bold mb-2">Technical Documentation</p>
            <p className="opacity-90">
              This architecture overview provides a high-level understanding of GovTrack's data flow, user roles, module interactions,
              and compliance enforcement mechanisms. For detailed technical specifications, API documentation, and integration guides,
              please contact the Prime Minister's Office IT Unit or download the complete System Architecture Document.
            </p>
          </div>
        </div>
      </Card>
    </div>
  );
};